import {createContext, FC, ReactNode, useContext} from 'react';
import {useSelector} from 'react-redux';
import Auth from '../Auth/Auth';
import {getCurrentUserId} from '@/lib/picos/currentLogin';
import type {RootState} from '@/@types/store';
import type {UserRoles} from '@/@types/schema';

type ReportAuthContextValue = {
    isAdmin: () => boolean;
    isOwner: () => boolean;
    hasRole: (role: UserRoles) => boolean;
    hasRoles: (roles: UserRoles[], mustHaveAll: boolean) => boolean;
    hasLegalRole: () => boolean;
    overrideRoles: UserRoles[] | null;
    setOverrideRoles: (roles: UserRoles[] | null) => void;
    isOverrideActive: boolean;
};

const ReportAuthContext = createContext<ReportAuthContextValue | null>(null);

type ReportAuthProviderProps = {
    children: ReactNode;
    overrideRoles: UserRoles[] | null;
    setOverrideRoles: (roles: UserRoles[] | null) => void;
};

export const ReportAuthProvider: FC<ReportAuthProviderProps> = ({children, overrideRoles, setOverrideRoles}) => {
    // Subscribe to roles so auth checks re-evaluate when roles load
    useSelector((state: RootState) => state.roles);
    const isOverrideActive = overrideRoles !== null;

    const isAdmin = (): boolean => {
        if (isOverrideActive) {
            return overrideRoles.includes('Administrator') || overrideRoles.includes('Owner');
        }
        const userId = getCurrentUserId();
        return userId ? Auth.isAdmin(userId) : false;
    };

    const isOwner = (): boolean => {
        if (isOverrideActive) {
            return overrideRoles.includes('Owner');
        }
        const userId = getCurrentUserId();
        return userId ? Auth.isOwner(userId) : false;
    };

    const hasRole = (role: UserRoles): boolean => {
        if (isOverrideActive) {
            return overrideRoles.includes(role);
        }
        const userId = getCurrentUserId();
        return userId ? Auth.hasRole(userId, role) : false;
    };

    const hasRoles = (roles: UserRoles[], mustHaveAll: boolean): boolean => {
        if (isOverrideActive) {
            if (mustHaveAll) return roles.every(role => overrideRoles.includes(role));
            else return roles.some(role => overrideRoles.includes(role));
        }
        const userId = getCurrentUserId();
        return userId ? Auth.hasRoles(userId, roles, mustHaveAll) : false;
    };

    const hasLegalRole = (): boolean => {
        if (isOverrideActive) {
            const legalRoles = ['Paralegal', 'Attorney', 'Legal Secretary'];
            return overrideRoles.some(r => legalRoles.includes(r));
        }
        const userId = getCurrentUserId();
        return userId ? Auth.hasLegalRole(userId) : false;
    };

    const value: ReportAuthContextValue = {
        isAdmin,
        isOwner,
        hasRole,
        hasRoles,
        hasLegalRole,
        overrideRoles,
        setOverrideRoles,
        isOverrideActive,
    };

    return (
        <ReportAuthContext.Provider value={value}>
            {children}
        </ReportAuthContext.Provider>
    );
};

export const useReportAuth = (): ReportAuthContextValue => {
    const context = useContext(ReportAuthContext);
    if (!context) {
        throw new Error('useReportAuth must be used within a ReportAuthProvider');
    }
    return context;
};
